import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Suspense } from 'react';
import { AppLayout } from '@/components/layouts/AppLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AuthProvider } from '@/contexts/AuthProvider';
import { TempAuthDataProvider } from '@/contexts/TempAuthDataContext';
import { NavigationBlockProvider } from '@/contexts/NavigationBlockContext';
import { FeatureVisibilityProvider } from '@/contexts/FeatureVisibilityContext';
import { OnboardingProgressProvider } from '@/contexts/OnboardingProgressContext';
import { ScrollToTop } from '@/components/ui/Page/ScrollToTop';
import { RouteErrorPage } from '@/components/ui/ErrorBoundary/RouteErrorPage';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { usePermissions } from '@/hooks/usePermissions';

// Smart redirect: admin+ goes to dashboard, others to make-orders
function DefaultRedirect() {
  const { canViewFinancials } = usePermissions();
  return <Navigate to={canViewFinancials ? '/dashboard' : '/make-orders'} replace />;
}

// Lazy load components with automatic chunk-load retry for deploy resilience
// Auth components
const SignUp = lazyWithRetry(() => import('@/components/auth/SignUp').then(m => ({ default: m.SignUp })));
const SignIn = lazyWithRetry(() => import('@/components/auth/SignIn').then(m => ({ default: m.SignIn })));
const AuthCallback = lazyWithRetry(() => import('@/components/auth/AuthCallback').then(m => ({ default: m.AuthCallback })));
const StripePayment = lazyWithRetry(() => import('@/components/auth/StripePayment').then(m => ({ default: m.StripePayment })));
const UserInfo = lazyWithRetry(() => import('@/components/auth/UserInfo').then(m => ({ default: m.UserInfo })));
const Congratulations = lazyWithRetry(() => import('@/components/auth/Congratulations').then(m => ({ default: m.Congratulations })));
const ProductionSetup = lazyWithRetry(() => import('@/components/auth/ProductionSetup').then(m => ({ default: m.ProductionSetup })));
const VerifyEmail = lazyWithRetry(() => import('@/components/auth/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const ForgotPassword = lazyWithRetry(() => import('@/components/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazyWithRetry(() => import('@/components/auth/ResetPassword').then(m => ({ default: m.ResetPassword })));
const StaffInvitePage = lazyWithRetry(() => import('@/components/auth/StaffInvitePage').then(m => ({ default: m.StaffInvitePage })));
const StaffOnboarding = lazyWithRetry(() => import('@/components/auth/StaffOnboarding').then(m => ({ default: m.StaffOnboarding })));

// Feature components
const IngredientPage = lazyWithRetry(() => import('@/features/Ingredients/Editor/IngredientPage'));
const IngredientsListPage = lazyWithRetry(() => import('@/features/Ingredients/List/IngredientsListPage').then(m => ({ default: m.IngredientsListPage })));
const MaterialPage = lazyWithRetry(() => import('@/features/Materials/Editor/MaterialPage'));
const MaterialsListPage = lazyWithRetry(() => import('@/features/Materials/List/MaterialsListPage').then(m => ({ default: m.MaterialsListPage })));
const SupplierPage = lazyWithRetry(() => import('@/features/Suppliers/Editor/SupplierPage'));
const SuppliersListPage = lazyWithRetry(() => import('@/features/Suppliers/List/SuppliersListPage').then(m => ({ default: m.SuppliersListPage })));
const RecipePage = lazyWithRetry(() => import('@/features/Recipes/Editor/RecipePage'));
const RecipesListPage = lazyWithRetry(() => import('@/features/Recipes/List/RecipesListPage').then(m => ({ default: m.RecipesListPage })));
const PackagePage = lazyWithRetry(() => import('@/features/Packages/Editor/PackagePage'));
const PackagesListPage = lazyWithRetry(() => import('@/features/Packages/List/PackagesListPage').then(m => ({ default: m.PackagesListPage })));
const ProductPage = lazyWithRetry(() => import('@/features/Products/Editor/ProductPage'));
const ProductsListPage = lazyWithRetry(() => import('@/features/Products/List/ProductsListPage').then(m => ({ default: m.ProductsListPage })));
const PurchaseOrdersListPage = lazyWithRetry(() => import('@/features/PurchaseOrders/List/PurchaseOrdersListPage').then(m => ({ default: m.PurchaseOrdersListPage })));
const PurchaseOrderPage = lazyWithRetry(() => import('@/features/PurchaseOrders/Editor/PurchaseOrderPage'));
const MakeOrdersListPage = lazyWithRetry(() => import('@/features/MakeOrders/List/MakeOrdersListPage').then(m => ({ default: m.MakeOrdersListPage })));
const MakeOrderPage = lazyWithRetry(() => import('@/features/MakeOrders/Editor/MakeOrderPage'));
const WeeklyLogPage = lazyWithRetry(() => import('@/features/WeeklyLogs/Editor/WeeklyLogPage').then(m => ({ default: m.WeeklyLogPage })));
const StaffListPage = lazyWithRetry(() => import('@/features/Staff/List/StaffListPage').then(m => ({ default: m.StaffListPage })));
const StaffPage = lazyWithRetry(() => import('@/features/Staff/Editor/StaffPage').then(m => ({ default: m.StaffPage })));
const CustomerPage = lazyWithRetry(() => import('@/features/Customers/Editor/CustomerPage'));
const RecurringOrderPage = lazyWithRetry(() => import('@/features/Customers/Editor/RecurringOrderPage'));
const CustomersListPage = lazyWithRetry(() => import('@/features/Customers/List/CustomersListPage').then(m => ({ default: m.CustomersListPage })));
const SalesOrdersListPage = lazyWithRetry(() => import('@/features/SalesOrders/List/SalesOrdersListPage').then(m => ({ default: m.SalesOrdersListPage })));
const SalesOrderPage = lazyWithRetry(() => import('@/features/SalesOrders/Editor/SalesOrderPage'));
const TransportOrdersListPage = lazyWithRetry(() => import('@/features/TransportOrders/List/TransportOrdersListPage').then(m => ({ default: m.TransportOrdersListPage })));
const TransportOrderPage = lazyWithRetry(() => import('@/features/TransportOrders/Editor/TransportOrderPage'));
const ProfilePage = lazyWithRetry(() => import('@/features/Profile/ProfilePage').then(m => ({ default: m.ProfilePage })));
const SettingsPage = lazyWithRetry(() => import('@/features/Settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const CompanySettingsPage = lazyWithRetry(() => import('@/features/Settings/CompanySettingsPage').then(m => ({ default: m.CompanySettingsPage })));
const ShippingSettingsPage = lazyWithRetry(() => import('@/features/Settings/ShippingSettingsPage').then(m => ({ default: m.ShippingSettingsPage })));
const OrderingScheduleSettingsPage = lazyWithRetry(() => import('@/features/Settings/OrderingScheduleSettingsPage').then(m => ({ default: m.OrderingScheduleSettingsPage })));
const PriceListSettingsPage = lazyWithRetry(() => import('@/features/Settings/PriceListSettingsPage').then(m => ({ default: m.PriceListSettingsPage })));
const PriceListEditorPage = lazyWithRetry(() => import('@/features/Settings/PriceListEditorPage').then(m => ({ default: m.PriceListEditorPage })));
const TaxRateSettingsPage = lazyWithRetry(() => import('@/features/Settings/TaxRateSettingsPage').then(m => ({ default: m.TaxRateSettingsPage })));
const ProductionSettingsPage = lazyWithRetry(() => import('@/features/Settings/ProductionSettingsPage'));
const SubscriptionSettingsPage = lazyWithRetry(() => import('@/features/Settings/SubscriptionSettingsPage'));
const DashboardPage = lazyWithRetry(() => import('@/features/Dashboard/DashboardPage'));

// Customer ordering portal (B5) — lazy so the buyer bundle (second Supabase
// client, portal auth context) never loads for operators.
const ProductionSheetPage = lazyWithRetry(() => import('@/features/ProductionSheet/ProductionSheetPage').then(m => ({ default: m.ProductionSheetPage })));
const BuyListPage = lazyWithRetry(() => import('@/features/BuyList/BuyListPage').then(m => ({ default: m.BuyListPage })));
const PortalRootLayout = lazyWithRetry(() => import('@/features/Portal/PortalRootLayout').then(m => ({ default: m.PortalRootLayout })));
const PortalProtectedRoute = lazyWithRetry(() => import('@/features/Portal/PortalProtectedRoute').then(m => ({ default: m.PortalProtectedRoute })));
const PortalHomePage = lazyWithRetry(() => import('@/features/Portal/Home/PortalHomePage').then(m => ({ default: m.PortalHomePage })));
const PortalOrderEditorPage = lazyWithRetry(() => import('@/features/Portal/Order/OrderEditorPage').then(m => ({ default: m.OrderEditorPage })));
const PortalPlacedOrderPage = lazyWithRetry(() => import('@/features/Portal/Order/PlacedOrderPage').then(m => ({ default: m.PlacedOrderPage })));
const TokenExchangePage = lazyWithRetry(() => import('@/features/Portal/Auth/TokenExchangePage').then(m => ({ default: m.TokenExchangePage })));
const RequestLinkPage = lazyWithRetry(() => import('@/features/Portal/Auth/RequestLinkPage').then(m => ({ default: m.RequestLinkPage })));
const PortalOrderHistoryPage = lazyWithRetry(() => import('@/features/Portal/History/OrderHistoryPage').then(m => ({ default: m.OrderHistoryPage })));
const PortalMenuPage = lazyWithRetry(() => import('@/features/Portal/Menu/MenuPage').then(m => ({ default: m.MenuPage })));
const PortalSequenceEditorPage = lazyWithRetry(() => import('@/features/Portal/Sequences/SequenceEditorPage').then(m => ({ default: m.SequenceEditorPage })));
const PortalRecurringOrdersPage = lazyWithRetry(() => import('@/features/Portal/Recurring/RecurringOrdersPage').then(m => ({ default: m.RecurringOrdersPage })));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Wrapper component for lazy-loaded routes
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

// Root Layout Component that provides context providers
function RootLayout() {
  return (
    <div className="App">
      <ScrollToTop />
      <AuthProvider>
        <TempAuthDataProvider>
          <NavigationBlockProvider>
            <Outlet />
          </NavigationBlockProvider>
        </TempAuthDataProvider>
      </AuthProvider>
    </div>
  );
}

// Protected App Layout Wrapper
function ProtectedAppLayout() {
  return (
    <ProtectedRoute>
      <FeatureVisibilityProvider>
        <OnboardingProgressProvider>
          <AppLayout />
        </OnboardingProgressProvider>
      </FeatureVisibilityProvider>
    </ProtectedRoute>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      // Public Auth Routes
      {
        path: "auth/signup",
        element: <LazyWrapper><SignUp /></LazyWrapper>
      },
      {
        path: "auth/signin",
        element: <LazyWrapper><SignIn /></LazyWrapper>
      },
      {
        path: "auth/callback",
        element: <LazyWrapper><AuthCallback /></LazyWrapper>
      },
      {
        path: "auth/verify-email",
        element: <LazyWrapper><VerifyEmail /></LazyWrapper>
      },
      // Onboarding Flow
      {
        path: "auth/payment",
        element: <LazyWrapper><StripePayment /></LazyWrapper>
      },
      {
        path: "auth/user-info",
        element: <LazyWrapper><UserInfo /></LazyWrapper>
      },
      {
        path: "auth/production-setup",
        element: <LazyWrapper><ProductionSetup /></LazyWrapper>
      },
      {
        path: "auth/congratulations",
        element: <LazyWrapper><Congratulations /></LazyWrapper>
      },
      // Password Recovery
      {
        path: "auth/forgot-password",
        element: <LazyWrapper><ForgotPassword /></LazyWrapper>
      },
      {
        path: "auth/reset-password",
        element: <LazyWrapper><ResetPassword /></LazyWrapper>
      },
      {
        path: "auth/staff-invite",
        element: <LazyWrapper><StaffInvitePage /></LazyWrapper>
      },
      {
        path: "auth/staff-onboarding",
        element: <LazyWrapper><StaffOnboarding /></LazyWrapper>
      },
      // Protected Routes - AppLayout wraps all authenticated routes
      {
        path: "/",
        element: <ProtectedAppLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          // Dashboard - admin+ only, others go to make-orders
          {
            index: true,
            element: <DefaultRedirect />
          },
          {
            path: "dashboard",
            element: <ProtectedRoute requiredRole="admin"><LazyWrapper><DashboardPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "company/dashboard",
            element: <Navigate to="/dashboard" replace />
          },
          {
            path: "reports",
            element: <Navigate to="/dashboard" replace />
          },
          // Ingredients Routes
          {
            path: "ingredients",
            element: <LazyWrapper><IngredientsListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "ingredients/new",
            element: <LazyWrapper><IngredientPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "ingredients/:id",
            element: <LazyWrapper><IngredientPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Materials Routes
          {
            path: "materials",
            element: <LazyWrapper><MaterialsListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "materials/new",
            element: <LazyWrapper><MaterialPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "materials/:id",
            element: <LazyWrapper><MaterialPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Suppliers Routes (supervisor+ access)
          {
            path: "suppliers",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><SuppliersListPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "suppliers/new",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><SupplierPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "suppliers/:id",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><SupplierPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          // Recipes Routes
          {
            path: "recipes",
            element: <LazyWrapper><RecipesListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "recipes/new",
            element: <LazyWrapper><RecipePage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "recipes/:id",
            element: <LazyWrapper><RecipePage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Packages Routes
          {
            path: "packages",
            element: <LazyWrapper><PackagesListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "packages/new",
            element: <LazyWrapper><PackagePage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "packages/:id",
            element: <LazyWrapper><PackagePage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Variants deprecated (A3): the standalone Variants feature is merged into the
          // Recipe page (outputs live on the OutputsCard). Redirect stale links to /recipes.
          { path: "variants", element: <Navigate to="/recipes" replace /> },
          { path: "variants/new", element: <Navigate to="/recipes" replace /> },
          { path: "variants/:id", element: <Navigate to="/recipes" replace /> },
          // Products Routes
          {
            path: "products",
            element: <LazyWrapper><ProductsListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "products/new",
            element: <LazyWrapper><ProductPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "products/:id",
            element: <LazyWrapper><ProductPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Purchase Orders Routes (supervisor+ access)
          {
            path: "purchase-orders",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><PurchaseOrdersListPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "purchase-orders/new",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><PurchaseOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "purchase-orders/:id",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><PurchaseOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          // Make Orders Routes
          {
            path: "make-orders",
            element: <LazyWrapper><MakeOrdersListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "make-orders/new",
            element: <LazyWrapper><MakeOrderPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Weekly production logs. Declared BEFORE `make-orders/:id` for readability — React
          // Router ranks static segments above dynamic ones, so `logs` cannot be eaten by `:id`.
          {
            path: "make-orders/logs/new",
            element: <ProtectedRoute requiredRole="admin"><LazyWrapper><WeeklyLogPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "make-orders/logs/:id",
            element: <ProtectedRoute requiredRole="admin"><LazyWrapper><WeeklyLogPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "make-orders/:id",
            element: <LazyWrapper><MakeOrderPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Staff Routes (supervisor+ access)
          {
            path: "staff",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><StaffListPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "staff/new",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><StaffPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "staff/:id",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><StaffPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "staff/:id/edit",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><StaffPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          // Customers Routes (supervisor+ access)
          {
            path: "customers",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><CustomersListPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "customers/new",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><CustomerPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "customers/:id",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><CustomerPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          // The recurring-order editor is a PAGE on both sides of the app now
          // (DECISION 32) — the buyer's lives at /portal/recurring/:id. A static
          // "new" segment outranks the :orderId param, so both resolve here.
          {
            path: "customers/:id/recurring/new",
            // DECISION 70 — Growth+. Only `new` is gated; `:orderId` below is
            // deliberately left open so a tenant can still open, pause and stop
            // orders they already have. Same split as transport-orders.
            element: <ProtectedRoute requiredRole="supervisor" requiredPlan="growth"><LazyWrapper><RecurringOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "customers/:id/recurring/:orderId",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><RecurringOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          // Sales Orders Routes (supervisor+)
          {
            path: "sales-orders",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><SalesOrdersListPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            /*
             * P6 — a normal operator page inside the app shell (D149 REVISED, session 244). It was
             * briefly chrome-free "so it prints as-is"; that optimised the occasional case (printing)
             * and broke the constant one — no nav, and no way back out. The print rules (`index.css`
             * plus `productionSheetPrint.css`) isolate the sheet at print time instead, which needs
             * nothing from the shell.
             */
            path: "production-sheet",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><ProductionSheetPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            // PRODUCTION_SHEET_BUY_LIST — reached only from the production sheet, same role.
            path: "buy-list",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><BuyListPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "sales-orders/new",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><SalesOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "sales-orders/:id",
            element: <ProtectedRoute requiredRole="supervisor"><LazyWrapper><SalesOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          // Transport Orders Routes
          {
            path: "transport-orders",
            element: <LazyWrapper><TransportOrdersListPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "transport-orders/new",
            element: <ProtectedRoute requiredPlan="growth"><LazyWrapper><TransportOrderPage /></LazyWrapper></ProtectedRoute>,
            errorElement: <RouteErrorPage />
          },
          {
            path: "transport-orders/:id",
            element: <LazyWrapper><TransportOrderPage /></LazyWrapper>,
            errorElement: <RouteErrorPage />
          },
          // Settings Routes
          {
            path: "settings",
            children: [
              {
                index: true,
                element: <LazyWrapper><SettingsPage /></LazyWrapper>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "profile",
                element: <LazyWrapper><ProfilePage /></LazyWrapper>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "company",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><CompanySettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "shipping",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><ShippingSettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "ordering-schedule",
                element: <ProtectedRoute requiredRole="admin" requiredPlan="growth"><LazyWrapper><OrderingScheduleSettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "price-lists",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><PriceListSettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "price-lists/new",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><PriceListEditorPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "price-lists/:id",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><PriceListEditorPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "tax-rate",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><TaxRateSettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "production",
                element: <ProtectedRoute requiredRole="admin"><LazyWrapper><ProductionSettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              },
              {
                path: "subscription",
                element: <ProtectedRoute requiredRole="owner"><LazyWrapper><SubscriptionSettingsPage /></LazyWrapper></ProtectedRoute>,
                errorElement: <RouteErrorPage />
              }
            ]
          }
          // TODO: Add other resource routes following same pattern
          // /variants, /packages, /products, etc.
        ]
      }
    ]
  },
  // Customer ordering portal (B5) — a SIBLING of the operator tree, not a child.
  // Buyers get their own Supabase client, auth context, and layout; nothing here
  // touches AuthProvider, AppLayout, or ProtectedRoute.
  {
    path: "/portal",
    element: <LazyWrapper><PortalRootLayout /></LazyWrapper>,
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: <LazyWrapper><PortalProtectedRoute><PortalHomePage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // The Menu is a peer of Orders, not a page underneath it — the two are
        // tabs, so each needs an address a tab can point at.
        path: "menu",
        element: <LazyWrapper><PortalProtectedRoute><PortalMenuPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        path: "history",
        // Reachable on a CLOSED portal (D70/S7) — the buyer's own record, and the
        // one destination the closed page offers.
        element: <LazyWrapper><PortalProtectedRoute allowWhenClosed><PortalOrderHistoryPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // What repeats, and where — the third tab (DECISION 17). A peer of
        // Orders and Menu, so like them it needs an address a tab can point at.
        path: "recurring",
        element: <LazyWrapper><PortalProtectedRoute><PortalRecurringOrdersPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // One delivery. The account and the delivery are URL segments, not page
        // state (VERDICT 3), so a landing card, a reminder deep link and a
        // browser Back button all address the same thing the same way. The
        // dateless form redirects to the soonest delivery worth typing into.
        path: "accounts/:customerId/deliveries",
        element: <LazyWrapper><PortalProtectedRoute><PortalOrderEditorPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // Placing a one-off (DECISION 35's *new* state). MUST stay above the
        // `:deliveryDate` route — a static segment only outranks a param when it
        // is declared first, and `new` is otherwise a perfectly good date param
        // that would load an empty delivery.
        path: "accounts/:customerId/deliveries/new",
        element: <LazyWrapper><PortalProtectedRoute><PortalOrderEditorPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        path: "accounts/:customerId/deliveries/:deliveryDate",
        element: <LazyWrapper><PortalProtectedRoute><PortalOrderEditorPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // An order the supplier already owns, read-only. DECISION 35 made
        // "closed" a LOCK rather than a different layout; this is the same idea
        // one step further along — once a draft is promoted it leaves
        // `portal_get_week` entirely, so the editor cannot render it and the
        // buyer lost sight of an order that had become MORE settled, not less.
        path: "accounts/:customerId/placed/:soNumber",
        // Same RPC as /portal/history, so it is the same exemption (D70/S7).
        element: <LazyWrapper><PortalProtectedRoute allowWhenClosed><PortalPlacedOrderPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // Starting one. Static segment first, so it outranks `:sequenceId`.
        // This is the buyer's create door since DECISION 38 retired AddDayModal
        // — which made recurring orders from the CONTENTS editor, and made them
        // empty. Days and products land together here, which is what lets D37
        // require products at creation.
        path: "accounts/:customerId/orders/new",
        element: <LazyWrapper><PortalProtectedRoute><PortalSequenceEditorPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // Editing the standing order itself, kept away from the dated grid so a
        // template change is always a deliberate act.
        path: "accounts/:customerId/orders/:sequenceId",
        element: <LazyWrapper><PortalProtectedRoute><PortalSequenceEditorPage /></PortalProtectedRoute></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        // Landing page for every emailed link — exchanges the token on click.
        path: "auth",
        element: <LazyWrapper><TokenExchangePage /></LazyWrapper>,
        errorElement: <RouteErrorPage />
      },
      {
        path: "request-link",
        element: <LazyWrapper><RequestLinkPage /></LazyWrapper>,
        errorElement: <RouteErrorPage />
      }
    ]
  }
]);